// /src/notificationsProvider.js

import React, {
  createContext, useContext, useEffect, useRef, useState, useCallback
} from "react";
import axios from "axios";
import { useAuth } from "./authContext";
import {
  loadDeliveredRegistry,
  pushDesktopNotification,
  isDelivered
} from "./DesktopNotificationManager";
import { notificationsSocket } from "../utils/Socket";
import { startHeartbeat, stopHeartbeat } from "./wsRecovery/heartbeat";
import {
  resumeAll, clearResumeRegistry, registerSocket
} from "./wsRecovery/wsResume";
import {
  setResumePointer, updateResumeSeq, clearResumeStore
} from "./wsRecovery/wsResumeStore";

const NotificationsContext = createContext();
export const useNotifications = () => useContext(NotificationsContext);

export const NotificationsProvider = ({ children }) => {
  const { isLoggedIn, userId } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);

  const lastSeqRef = useRef(0);

  // English comment: load desktop-delivered registry
  useEffect(() => {
    loadDeliveredRegistry();
  }, []);

  // English comment: compute unread indicator
  const recomputeUnread = useCallback((list) => {
    setHasUnread(list.some((n) => n.status === "unread"));
  }, []);

  // English comment: reset all state on logout
  const resetState = useCallback(() => {
    setNotifications([]);
    setHasUnread(false);
    lastSeqRef.current = 0;
    clearResumeRegistry();
    clearResumeStore();
    try { notificationsSocket.disconnect(); } catch {}
  }, []);

  // English comment: fetch full list
  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.post(
        "/api/notification/list",
        {},
        { withCredentials: true }
      );

      const list = data?.items ?? [];
      setNotifications(list);
      recomputeUnread(list);

      // English comment: push desktop notification for new ones only
      list.forEach((n) => {
        if (!isDelivered(n.id)) {
          pushDesktopNotification(n);
        }
      });

    } catch (err) {
      console.error("[NOTIF] fetchAll failed:", err);
    }
  }, [recomputeUnread]);

  // English comment: optimistic remove
  const optimisticArchive = useCallback((id) => {
    setNotifications((prev) => {
      const next = prev.filter((n) => n.id !== id);
      recomputeUnread(next);
      return next;
    });
  }, [recomputeUnread]);

  // English comment: optimistic mark-read
  const optimisticMarkRead = useCallback((id) => {
    setNotifications((prev) => {
      const next = prev.map((n) =>
        n.id === id ? { ...n, status: "read" } : n
      );
      recomputeUnread(next);
      return next;
    });
  }, [recomputeUnread]);

  // -------------------------------------------------------------
  // English comment: WS lifecycle + WS resume
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isLoggedIn) {
      resetState();
      return;
    }

    const socket = notificationsSocket;

    if (!socket.connected) {
      socket.connect();
      startHeartbeat(socket);
    }

    clearResumeRegistry();
    clearResumeStore();

    // English comment: track resume pointer (no userId in payload)
    setResumePointer(`notifications`, {});

    // English comment: register WS-resume handler (missing events)
    registerSocket(`notifications`, async () => {
      try {
        const { data } = await axios.post(
          "/api/ws-resume/notifications",
          { sinceSeq: lastSeqRef.current },
          { withCredentials: true }
        );

        if (data?.ok && data.events?.length) {
          const recovered = data.events;

          setNotifications((prev) => {
            const merged = [...recovered, ...prev];
            recomputeUnread(merged);
            return merged;
          });

          const maxSeq = Math.max(...recovered.map(e => e.seq), 0);
          lastSeqRef.current = Math.max(lastSeqRef.current, maxSeq);
        }
      } catch (err) {
        console.error("[NOTIF] resume failed:", err);
      }
    });

    const onConnect = async () => {
      await fetchAll();

      // English comment: server-side optional resume (token-based)
      notificationsSocket.emit("notifications:resume", {
        lastSeen: lastSeqRef.current,
      });

      resumeAll();
    };

    notificationsSocket.on("connect", onConnect);

    notificationsSocket.on("notifications:new", ({ seq, notification }) => {
      lastSeqRef.current = Math.max(lastSeqRef.current, seq);
      updateResumeSeq(`notifications`, seq);

      pushDesktopNotification(notification);

      setNotifications((prev) => {
        const merged = [notification, ...prev];
        recomputeUnread(merged);
        return merged;
      });
    });

    notificationsSocket.on("notifications:update", ({ seq, notification }) => {
      lastSeqRef.current = Math.max(lastSeqRef.current, seq);
      updateResumeSeq(`notifications`, seq);

      setNotifications((prev) => {
        const updated = prev.map((n) =>
          n.id === notification.id ? notification : n
        );
        recomputeUnread(updated);
        return updated;
      });
    });

    return () => {
      stopHeartbeat(notificationsSocket);
      notificationsSocket.off("connect", onConnect);
      notificationsSocket.off("notifications:new");
      notificationsSocket.off("notifications:update");
    };
  }, [isLoggedIn, userId, fetchAll, recomputeUnread, resetState]);

  // English comment: initial fetch
  useEffect(() => {
    if (isLoggedIn) {
      fetchAll();
    } else {
      resetState();
    }
  }, [isLoggedIn, fetchAll, resetState]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        hasUnread,
        refreshNotifications: fetchAll,
        optimisticArchive,
        optimisticMarkRead,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
